/**
 * Decléor — stop a Flickity carousel clipping the bottom of its cards.
 *
 * THE SYMPTOM: on some page loads the product carousel cuts its cards off horizontally, straight
 * through the rating line, identically across every visible card.
 *
 * THE MECHANISM, which is certain: Flickity measures the tallest cell ONCE and writes the result as an
 * inline `height` on `.flickity-viewport`, which is `overflow: hidden`. Measured on this store, that
 * value is `height: 408.266px` — a fraction, i.e. read off a real box. Any load where that measurement
 * comes in short leaves the bottom of every card behind the viewport's edge, and nothing ever
 * re-measures: Flickity only recomputes on a window resize.
 *
 * THE RACE IS REAL, and catching it took reading the height at the moment Flickity writes it rather
 * than after the page settled. Sampled at `document.readyState === 'interactive'`, Flickity's FIRST
 * measurement on this carousel is **347.906px**. The correct height is 408.266px. It is short by 60px —
 * about the height of the rating-and-price row, which is exactly where the screenshot cuts. On a normal
 * load something later re-measures and the inline height becomes 408.266px; on a load where that second
 * pass does not land, the 347.906px stays and every card is clipped at the same line.
 *
 * Ruled out along the way: the ratings are server-rendered (12 `.product__rating` present at 2s, zero
 * Judge.me `.jdgm-prev-badge` injected later) and every image carries width/height, so neither is what
 * grows the cell afterwards. What remains short at `interactive` was not pinned down further — and does
 * not need to be, because the correction below is keyed to the symptom (a cell taller than its
 * viewport) rather than to any one cause.
 *
 * WHY IT WRITES THE HEIGHT RATHER THAN DISPATCHING A RESIZE. A synthetic `window.resize` would make
 * Flickity recompute everything, which is tempting, but that event is load-bearing elsewhere in this
 * theme — assets/drawer-viewport.js closes the menu drawer on it, sticky-atc.js re-runs its rAF loop,
 * and theme.js recomputes `js__show__mobile`. Firing it as a side effect of a layout fix would be a
 * poor trade. `height` on the viewport is the very property Flickity itself sets, so writing a better
 * measurement into it is correcting that value, not fighting another system — and Flickity overwrites
 * it on the next real resize, by which point its own measurement is correct too.
 *
 * Runs at `load` and again on `document.fonts.ready`, because those are the two moments a cell can
 * finish growing. Idempotent: it writes nothing unless a cell actually hangs below the viewport.
 * No-op on a page with no carousel.
 */
(function () {
  /* 1px of tolerance: both numbers are sub-pixel (the measured 408.266 against a cell's 408.3), so an
     exact comparison would rewrite the height on every pass for no reason. */
  var SLACK = 1;

  function fix(viewport) {
    var track = viewport.firstElementChild;
    if (!track) {
      return;
    }
    var cells = track.children;
    var tallest = 0;
    var i;
    var h;
    for (i = 0; i < cells.length; i += 1) {
      h = cells[i].getBoundingClientRect().height;
      if (h > tallest) {
        tallest = h;
      }
    }
    if (!tallest) {
      return;
    }
    /* offsetHeight, not the inline style: the clip is only real if the BOX is shorter than its content,
       and the inline value may be absent on a carousel Flickity has disabled at this breakpoint
       (watchCSS), where there is nothing to correct. */
    if (viewport.offsetHeight && tallest > viewport.offsetHeight + SLACK) {
      viewport.style.height = tallest + 'px';
    }
  }

  function sweep() {
    var viewports = document.querySelectorAll('.flickity-viewport');
    var i;
    for (i = 0; i < viewports.length; i += 1) {
      fix(viewports[i]);
    }
  }

  if (document.readyState === 'complete') {
    sweep();
  } else {
    window.addEventListener('load', sweep, { once: true });
  }

  if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
    document.fonts.ready.then(sweep);
  }
})();
